import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Box,
  IconButton,
  Tooltip,
  Dialog,
  DialogContent,
  CircularProgress,
  Tabs,
  Tab,
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import DeveloperBoardIcon from '@mui/icons-material/DeveloperBoard';
import ModalHeader from '../../../shared/components/ModalHeader';
import ModalFooter from '../../../shared/components/ModalFooter';
import EmptyStateMessage from '../../../shared/components/EmptyStateMessage';
import { humanizeResourceParts } from '../../../shared/labels';
import { resolveTriggerReconcilerRpc } from '../../../shared/utils/reconcilerTrigger';
import ReconcilerListPanel from '../components/pipeline/ReconcilerListPanel';
import type { ReconcilerListItem } from '../components/pipeline/ReconcilerListPanel';
import type { Customer } from '../../../types';
import type { ReconcilerExecution } from '../../../types/frontend';
import type { ReconcilerHealth } from '../../../types/backend';
import ReconcilerDetailPane from '../components/pipeline/ReconcilerDetailPane';
import ControlPlaneOverviewPanel from '../components/ControlPlaneOverviewPanel';
import CustomerLensPanel from '../components/CustomerLensPanel';
import ActivityLogPanel from '../components/ActivityLogPanel';
import FeatureSwitchesPanel from '../components/FeatureSwitchesPanel';
import DriftOverviewPanel from '../components/DriftOverviewPanel';
import DeploymentMapPanel from '../components/DeploymentMapPanel';
import { useKernelView } from '../hooks/useKernelView';
import { useControlPlaneHealth } from '../hooks/useControlPlaneHealth';
import { useCustomerControlPlane } from '../hooks/useCustomerControlPlane';
import { useOpsService } from '../../../shared/api/opsService';
import { useFeatureSwitch } from '../../../shared/hooks/useFeatureSwitch';
import { useInvestigation } from '../../investigation/context/InvestigationContext';

// TODO: promote the Kernel modal to a dedicated /kernel route (deferred — low priority).

interface KernelModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const KernelModal: React.FC<KernelModalProps> = ({ isOpen, onClose }) => {
  const [activeTab, setActiveTab] = useState(0);
  const [selectedReconciler, setSelectedReconciler] = useState<string | null>(null);
  const {
    reconcilerNames,
    selectedExecutions,
    loading,
    detailLoading,
    refreshList,
    loadDetail,
  } = useKernelView();
  const {
    reconcilers: healthReconcilers,
    loading: healthLoading,
    error: healthError,
    refresh: refreshHealth,
  } = useControlPlaneHealth();
  const { selectedCustomer: appCustomer, customers, loadCustomers } = useInvestigation();
  // Local lens: which customer this admin view inspects. Defaults to the app's
  // active customer but stays independent of it — the kernel modal must never
  // mutate the global customer.
  const [lensCustomer, setLensCustomer] = useState<Customer | null>(null);
  const activeCustomer = lensCustomer ?? appCustomer;

  // Reuse the already-loaded customer roster to decode resource ids into human
  // labels (customer NAME · rule · event). NOTE: the roster currently lives only
  // in InvestigationContext — see the surfaced follow-up to extract a shared one.
  const customerNameById = useMemo(
    () => new Map(customers.map((c) => [c.id, c.name])),
    [customers],
  );
  const formatResourceParts = useCallback(
    (resourceId: string) => humanizeResourceParts(resourceId, customerNameById),
    [customerNameById],
  );
  const {
    reconcilers: customerReconcilers,
    skippedDomains,
    loading: customerLoading,
    error: customerError,
    refresh: refreshCustomer,
  } = useCustomerControlPlane(activeCustomer?.id ?? null);
  const ops = useOpsService();
  const baselineEnabled = useFeatureSwitch('account_baseline');

  useEffect(() => {
    if (isOpen) {
      refreshList();
      refreshHealth();
    }
  }, [isOpen, refreshList, refreshHealth]);

  // Ensure the customer roster is loaded so resource_ids decode to customer
  // names — the Kernel must not rely on another feature having triggered it.
  useEffect(() => {
    if (isOpen && customers.length === 0) {
      loadCustomers().catch(() => {
        /* labels gracefully fall back to the raw id */
      });
    }
  }, [isOpen, customers.length, loadCustomers]);

  useEffect(() => {
    if (isOpen) refreshCustomer();
  }, [isOpen, refreshCustomer]);

  const handleSelect = useCallback((name: string) => {
    setSelectedReconciler(name);
    loadDetail(name);
  }, [loadDetail]);

  const handleTileSelect = useCallback((name: string) => {
    setSelectedReconciler(name);
    loadDetail(name);
    setActiveTab(2);
  }, [loadDetail]);

  const handleSelectCustomer = useCallback((customerId: string) => {
    const existing = customers.find((c) => c.id === customerId);
    if (existing) {
      setLensCustomer(existing);
    }
  }, [customers]);

  const handlePause = useCallback(async (domain: string) => {
    await ops.pauseReconciler(domain);
    refreshList();
  }, [ops, refreshList]);

  const handleResume = useCallback(async (domain: string) => {
    await ops.resumeReconciler(domain);
    refreshList();
  }, [ops, refreshList]);

  const handleTrigger = useCallback(async (domain: string) => {
    await ops.triggerReconciler(domain);
    setTimeout(() => loadDetail(domain), 2000);
  }, [ops, loadDetail]);

  const handleRefresh = useCallback(() => {
    refreshList();
    refreshHealth();
    refreshCustomer();
    if (selectedReconciler) loadDetail(selectedReconciler);
  }, [refreshList, refreshHealth, refreshCustomer, loadDetail, selectedReconciler]);

  const healthByDomain: Record<string, ReconcilerHealth> = {};
  for (const h of healthReconcilers) healthByDomain[h.domain] = h;

  const listItems: ReconcilerListItem[] = reconcilerNames.map((name) => ({
    name,
    health: healthByDomain[name],
  }));

  return (
    <Dialog
      open={isOpen}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      slotProps={{
        paper: { sx: { height: '90vh', maxHeight: '90vh' } },
      }}
    >
      <ModalHeader
        icon={DeveloperBoardIcon}
        title="Kernel"
        onClose={onClose}
        desktopRecommended
        actions={
          activeTab === 0 || activeTab === 1 || activeTab === 2 ? (
            <Tooltip title="Refresh">
              <IconButton onClick={handleRefresh} size="small">
                <RefreshIcon />
              </IconButton>
            </Tooltip>
          ) : undefined
        }
      />

      <Box sx={{ borderBottom: 1, borderColor: 'divider', px: 2 }}>
        <Tabs
          value={activeTab}
          onChange={(_, v) => setActiveTab(v)}
          variant="scrollable"
          scrollButtons="auto"
        >
          <Tab label="Control Plane" />
          <Tab label="Customer" />
          <Tab label="Reconcilers" />
          <Tab label="Activity Log" />
          <Tab label="Feature Switches" />
          <Tab label="Drift" />
          <Tab label="Deployment Map" />
        </Tabs>
      </Box>

      <DialogContent sx={{ p: 0, display: 'flex', flexDirection: 'column', height: '100%', overflow: 'auto' }}>
        {activeTab === 0 && (
          <ControlPlaneOverviewPanel
            reconcilers={healthReconcilers}
            loading={healthLoading}
            error={healthError}
            selectedDomain={selectedReconciler}
            onSelect={handleTileSelect}
          />
        )}
        {activeTab === 1 && (
          <CustomerLensPanel
            selectedCustomer={activeCustomer}
            customers={customers}
            reconcilers={customerReconcilers}
            skippedDomains={skippedDomains}
            loading={customerLoading}
            error={customerError}
            selectedDomain={selectedReconciler}
            onSelectCustomer={handleSelectCustomer}
            onSelectReconciler={handleTileSelect}
          />
        )}
        {activeTab === 2 && (
          <ReconcilersTabContent
            loading={loading}
            listItems={listItems}
            selectedReconciler={selectedReconciler}
            selectedExecutions={selectedExecutions}
            healthByDomain={healthByDomain}
            detailLoading={detailLoading}
            formatResourceParts={formatResourceParts}
            onSelect={handleSelect}
            onPause={handlePause}
            onResume={handleResume}
            onTrigger={handleTrigger}
            baselineEnabled={baselineEnabled}
          />
        )}
        {activeTab === 3 && <ActivityLogPanel />}
        {activeTab === 4 && <FeatureSwitchesPanel />}
        {activeTab === 5 && <DriftOverviewPanel />}
        {activeTab === 6 && <DeploymentMapPanel />}
      </DialogContent>

      {activeTab === 2 && (
        <ModalFooter
          count={reconcilerNames.length}
          label={`reconciler${reconcilerNames.length !== 1 ? 's' : ''}`}
          onClose={onClose}
        />
      )}
    </Dialog>
  );
};

/**
 * Mirrors opsService.triggerReconciler resolvability: only these have a trigger RPC.
 * account_baseline.TriggerReconciler is refused while its switch is off, so the
 * baseline reconciler offers no Run Now then.
 */
function canTriggerReconciler(domain: string, baselineEnabled: boolean): boolean {
  const rpc = resolveTriggerReconcilerRpc(domain);
  if (rpc === 'account_baseline.TriggerReconciler') return baselineEnabled;
  return rpc !== null;
}

interface ReconcilersTabContentProps {
  loading: boolean;
  listItems: ReconcilerListItem[];
  selectedReconciler: string | null;
  selectedExecutions: ReconcilerExecution[];
  healthByDomain: Record<string, ReconcilerHealth>;
  detailLoading: boolean;
  formatResourceParts: (resourceId: string) => string[];
  onSelect: (name: string) => void;
  onPause: (domain: string) => Promise<void>;
  onResume: (domain: string) => Promise<void>;
  onTrigger: (domain: string) => Promise<void>;
  baselineEnabled: boolean;
}

const ReconcilersTabContent: React.FC<ReconcilersTabContentProps> = ({
  loading,
  listItems,
  selectedReconciler,
  selectedExecutions,
  healthByDomain,
  detailLoading,
  formatResourceParts,
  onSelect,
  onPause,
  onResume,
  onTrigger,
  baselineEnabled,
}) => {
  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', width: '100%', py: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', flex: 1, overflow: 'hidden' }}>
      <Box sx={{ display: 'flex', flex: 1, overflow: 'hidden' }}>
        <Box
          sx={{
            width: 280,
            borderRight: 1,
            borderColor: 'divider',
            display: 'flex',
            flexDirection: 'column',
            bgcolor: 'background.default',
          }}
        >
          <ReconcilerListPanel
            items={listItems}
            selectedName={selectedReconciler}
            onSelect={onSelect}
          />
        </Box>

        <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'auto' }}>
          {selectedReconciler ? (
            <ReconcilerDetailPane
              name={selectedReconciler}
              executions={selectedExecutions}
              health={healthByDomain[selectedReconciler]}
              canTrigger={canTriggerReconciler(selectedReconciler, baselineEnabled)}
              detailLoading={detailLoading}
              formatResourceParts={formatResourceParts}
              onPause={onPause}
              onResume={onResume}
              onTrigger={onTrigger}
            />
          ) : (
            <EmptyStateMessage
              icon={DeveloperBoardIcon}
              title="No reconciler selected"
              description="Select a reconciler from the list to view execution details"
            />
          )}
        </Box>
      </Box>
    </Box>
  );
};

export default KernelModal;
